import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import axiosInstance from '@/lib/axios';
import { eventStorage } from '@/lib/eventStorage';
import type { EventOverview } from '@/pages/events/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EventContextType {
  selectedEventId: string | null;
  setSelectedEventId: (id: string | null) => void;
  selectedEvent: EventOverview | null;
  /** Whether {@link selectedEvent}'s by-id resolution is still in flight. */
  isLoadingSelectedEvent: boolean;
  /** The current server page of events (top {@link PAGE_SIZE}, filtered by {@link searchTerm}). */
  events: EventOverview[];
  isLoadingEvents: boolean;
  isErrorEvents: boolean;
  /** The event-selector search box value; updating it re-queries the server. */
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

/** Server page size for the selector — never load the whole (multi-thousand) event set. */
const PAGE_SIZE = 20;

/** Debounce before a keystroke turns into a server `$filter` requery. */
const SEARCH_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// Scoped-route helpers
// ---------------------------------------------------------------------------

/**
 * Routes whose content is scoped to the currently selected event. `/events` is
 * the legacy AngularJS / Web.React shape (`/events/:eventId/orders[/:orderId]`)
 * — the event lives in the path there, and `useRouteEventSync` folds it into
 * the selection, so those URLs are event-scoped too.
 */
export const EVENT_SCOPED_ROUTES = ['/orders', '/operations', '/events'] as const;

export function isEventScopedRoute(pathname: string): boolean {
  return EVENT_SCOPED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(route + '/'),
  );
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const EventContext = createContext<EventContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// Data fetching (server-side paging + filtering — the EventsOverview OData set
// is `[EnableQuery]`, so $top/$orderby/$filter are honoured on the server).
// ---------------------------------------------------------------------------

/**
 * One page of events, name-ordered, optionally filtered by a substring across
 * the fields the option label shows (see getEventDisplayLabel): name, status,
 * and site — so a site/status search (e.g. "Site A", "Open") still finds events,
 * not just a name match. The nullable fields are `ne null` guarded because
 * [EnableQuery] applies this $filter in memory, where `tolower(null)` would throw.
 */
async function fetchEventsPage(search: string): Promise<EventOverview[]> {
  const parts = [`$top=${PAGE_SIZE}`, '$orderby=name', '$count=true'];
  const term = search.trim().toLowerCase().replace(/'/g, "''");
  if (term) {
    const clause = [
      `contains(tolower(name), '${term}')`,
      `(eventStatus ne null and contains(tolower(eventStatus), '${term}'))`,
      `(siteName ne null and contains(tolower(siteName), '${term}'))`,
    ].join(' or ');
    parts.push(`$filter=${encodeURIComponent(clause)}`);
  }
  const response = await axiosInstance.get<{ value?: EventOverview[] }>(
    `/odata/EventsOverview?${parts.join('&')}`,
  );
  return response.data?.value ?? [];
}

/**
 * Resolves a single event by id, independent of the paged/filtered list — the
 * selection persists across reloads and may not be in the current 20, but the
 * trigger caption / event badge still need its full record.
 */
async function fetchEventById(id: string): Promise<EventOverview | null> {
  const filter = encodeURIComponent(`id eq ${id}`);
  const response = await axiosInstance.get<{ value?: EventOverview[] }>(
    `/odata/EventsOverview?$filter=${filter}&$top=1`,
  );
  return response.data?.value?.[0] ?? null;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function EventProvider({ children }: { children: ReactNode }): ReactNode {
  const [selectedEventId, setSelectedEventIdState] = useState<string | null>(() =>
    eventStorage.getSelectedEventId(),
  );

  // Raw box value (updates on every keystroke) + the debounced value the query keys on.
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  const {
    data: events = [],
    isLoading: isLoadingEvents,
    isError: isErrorEvents,
  } = useQuery<EventOverview[]>({
    queryKey: ['eventContext', 'EventsOverview', debouncedSearch],
    queryFn: () => fetchEventsPage(debouncedSearch),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // Keep the prior page visible while a new search resolves — no flicker to empty.
    placeholderData: keepPreviousData,
  });

  const setSelectedEventId = useCallback((id: string | null) => {
    setSelectedEventIdState(id);
    eventStorage.setSelectedEventId(id);
  }, []);

  // The selected event resolved by id (may not be in the current page).
  const {
    data: selectedEventData,
    isLoading: isLoadingSelected,
    isError: isErrorSelected,
  } = useQuery<EventOverview | null>({
    queryKey: ['eventContext', 'event', selectedEventId],
    queryFn: () => fetchEventById(selectedEventId as string),
    enabled: Boolean(selectedEventId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const selectedEvent = selectedEventId ? (selectedEventData ?? null) : null;

  // Drop a persisted id that no longer resolves to an accessible event (deleted /
  // archived, or a stale value carried over from another site). Only act once the
  // by-id resolution has genuinely settled — a transient failure or the initial
  // in-flight state (e.g. a legacy `/events/:eventId/orders` deep link landing
  // before the query resolves) must not clear a valid selection.
  useEffect(() => {
    if (!selectedEventId || isLoadingSelected || isErrorSelected) return;
    if (selectedEventData === null) setSelectedEventId(null);
  }, [selectedEventId, isLoadingSelected, isErrorSelected, selectedEventData, setSelectedEventId]);

  const value = useMemo<EventContextType>(
    () => ({
      selectedEventId,
      setSelectedEventId,
      selectedEvent,
      isLoadingSelectedEvent: isLoadingSelected,
      events,
      isLoadingEvents,
      isErrorEvents,
      searchTerm,
      setSearchTerm,
    }),
    [
      selectedEventId,
      setSelectedEventId,
      selectedEvent,
      isLoadingSelected,
      events,
      isLoadingEvents,
      isErrorEvents,
      searchTerm,
    ],
  );

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useEventContext(): EventContextType {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEventContext must be used within an EventProvider');
  }
  return context;
}
